import React from 'react';
import StageProgressBreakdown from './StageProgressBreakdown/StageProgressBreakdown';

import * as styles from './StageDetail.module.scss';
import { Statistics, Stage } from './Home';
import ReviewLinkContext from './ReviewLinkContext';
import { useReviewSummaryContext } from './ReviewSummaryContext';
import { StageProgressSummary } from './StageProgressSummary/StageProgressSummary';
import { STAGE_ACTION_CONFIG } from './progressConfig';
import { RetryButton, StageStatisticsStatus } from './stageStatistics';
import { Inline, Stack } from 'components/layout';
import {
  Button,
  Heading,
  Icon,
  IconList,
  Loading,
  Text,
} from 'components/core';

export type StageDetailProps = {
  stage: Stage;
  stats: Statistics;
  status: StageStatisticsStatus;
  reviewerName: string;
  studyCategoryLink: string;
} & React.HTMLAttributes<HTMLDivElement>;

const StageDetail = ({
  stage,
  stats,
  status,
  reviewerName,
  studyCategoryLink,
  ...divProps
}: StageDetailProps) => {
  const {
    hasExtractionTemplates,
    includedStudyCount,
  } = useReviewSummaryContext();
  const isComplete = stats?.consensusRequired + stats?.available == 0;
  const notStarted =
    stats?.complete +
      stats?.partiallyComplete +
      stats?.consensusRequired +
      stats?.notStarted ==
    0;

  const teamSettingsLabel = {
    screen: 'Title & Abstract team settings',
    select: 'Full Text Review team settings',
    included: 'Extraction team settings',
  };

  const teamSettingsButton = (reviewLink: string) => (
    <Button
      to={`${reviewLink}/team#${stage}`}
      size="sm"
      variant="ghost"
      type="brand"
      aria-label={teamSettingsLabel[stage]}
    >
      <Icon icon={IconList.solid.faGear} />
      Team settings
    </Button>
  );

  if (status.counters !== 'success') {
    const { primaryAction, primaryActionLabel } = STAGE_ACTION_CONFIG[stage];
    const hasFailed = status.counters === 'error';

    return (
      <ReviewLinkContext.Consumer>
        {(reviewLink) => (
          <div {...divProps}>
            <Inline alignX="left" spacing={10} className={styles.progressPanel}>
              <Stack spacing={5} alignX="left" className={styles.panelSection}>
                <Heading type="h4">Team progress</Heading>
                <div role="status">
                  {hasFailed && !status.isRetrying ? (
                    <Text>Couldn&apos;t load statistics.</Text>
                  ) : (
                    <Loading size="lg" />
                  )}
                </div>
                {hasFailed && <RetryButton status={status} />}
                {teamSettingsButton(reviewLink)}
              </Stack>
              <Inline alignX="center" className={styles.panelSection}>
                <Stack alignX="center" spacing={4}>
                  <Heading type="h4" component="p">
                    {reviewerName}, continue to {primaryAction}
                  </Heading>
                  <Button
                    to={studyCategoryLink}
                    size="md"
                    type="brand"
                    variant="solid"
                    aria-label={primaryActionLabel}
                  >
                    Continue
                  </Button>
                </Stack>
              </Inline>
            </Inline>
          </div>
        )}
      </ReviewLinkContext.Consumer>
    );
  }

  const showCreateTemplateEntryPoint =
    stage === 'included' && !hasExtractionTemplates && includedStudyCount > 0;

  return (
    <ReviewLinkContext.Consumer>
      {(reviewLink) => (
        <div {...divProps}>
          <Inline alignX="left" spacing={10} className={styles.progressPanel}>
            <Stack spacing={5} alignX="left" className={styles.panelSection}>
              <Heading type="h4">Team progress</Heading>
              <StageProgressBreakdown stage={stage} {...stats} />
              {teamSettingsButton(reviewLink)}
            </Stack>
            <Inline alignX="center" className={styles.panelSection}>
              <StageProgressSummary
                stage={stage}
                isStageNotStarted={notStarted}
                isStageComplete={isComplete}
                reviewerName={reviewerName}
                contributionCount={stats?.contribution}
                contributionStatus={status.contribution}
                contributionRetry={
                  <RetryButton
                    status={status}
                    variant="ghost"
                    label="Retry loading your contribution"
                  />
                }
                availableStudyCount={stats?.available}
                pendingConsensusCount={stats?.consensusRequired}
                studyListUrl={studyCategoryLink}
                chooseYourTemplateUrl={
                  showCreateTemplateEntryPoint
                    ? `${reviewLink}/choose_your_template?type=de&backTo=review_summary`
                    : undefined
                }
              />
            </Inline>
          </Inline>
        </div>
      )}
    </ReviewLinkContext.Consumer>
  );
};

export default StageDetail;
